import type { RandomSource } from './rng.js';
import {
  WALL,
  type CellState,
  type ColorId,
  type Pos,
  createEmptyGrid,
  isInBounds,
  posEqual,
} from './types.js';

export interface SolverPair {
  color: ColorId;
  a: Pos;
  b: Pos;
}

export interface SolverResult {
  solvable: boolean;
  unique: boolean;
  solution?: Map<ColorId, Pos[]>;
  solutionCount: number;
}

const DIRECTIONS: Pos[] = [
  [0, -1],
  [0, 1],
  [-1, 0],
  [1, 0],
];

function shuffle<T>(items: T[], rng: RandomSource): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export interface GeneratedSolution {
  paths: Map<ColorId, Pos[]>;
  /** Cells excluded from the puzzle this attempt — same shape as `Level.walls`. */
  walls: Pos[];
}

export function generateSolution(
  size: number,
  pairCount: number,
  wallCount = 0,
  rng: RandomSource = Math.random,
): GeneratedSolution | null {
  const colors = Array.from({ length: pairCount }, (_, i) => `c${i + 1}`);
  const allCells: Pos[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      allCells.push([x, y]);
    }
  }

  for (let attempt = 0; attempt < 200; attempt++) {
    const shuffled = shuffle(allCells, rng);
    const walls = shuffled.slice(0, wallCount);
    const starts = shuffled.slice(wallCount, wallCount + pairCount);
    const paths = new Map<ColorId, Pos[]>();
    const owner = createEmptyGrid(size);

    for (const [x, y] of walls) {
      owner[y][x] = WALL;
    }

    for (let i = 0; i < pairCount; i++) {
      const start = starts[i];
      paths.set(colors[i], [start]);
      owner[start[1]][start[0]] = colors[i];
    }

    let unassigned = size * size - pairCount - wallCount;
    let progress = true;

    while (unassigned > 0 && progress) {
      progress = false;
      for (const color of colors) {
        if (unassigned === 0) break;

        const path = paths.get(color)!;
        const head = path[path.length - 1];
        const neighbors = shuffle(DIRECTIONS, rng)
          .map(([dx, dy]): Pos => [head[0] + dx, head[1] + dy])
          .filter((pos) => {
            if (!isInBounds(pos, size)) return false;
            const [x, y] = pos;
            return owner[y][x] === null;
          });

        if (neighbors.length === 0) continue;

        const next = neighbors[0];
        path.push(next);
        owner[next[1]][next[0]] = color;
        unassigned--;
        progress = true;
      }
    }

    if (unassigned === 0) {
      return { paths, walls };
    }
  }

  return null;
}

export function solutionToPairs(solution: Map<ColorId, Pos[]>): SolverPair[] {
  const pairs: SolverPair[] = [];
  for (const [color, path] of solution) {
    if (path.length < 2) continue;
    pairs.push({
      color,
      a: path[0],
      b: path[path.length - 1],
    });
  }
  return pairs;
}

function keyOf(p: Pos): string {
  return `${p[0]},${p[1]}`;
}

interface HeadState {
  color: ColorId;
  head: Pos;
  target: Pos;
  path: Pos[];
  resolved: boolean;
}

/** Legal next cells for a growing path: empty cells, or its own (still unreached) target. */
function legalMoves(state: HeadState, grid: CellState[][], size: number): Pos[] {
  const moves: Pos[] = [];
  for (const [dx, dy] of DIRECTIONS) {
    const next: Pos = [state.head[0] + dx, state.head[1] + dy];
    if (!isInBounds(next, size)) continue;
    const cell = grid[next[1]][next[0]];
    if (cell === null) {
      moves.push(next);
    } else if (cell === state.color && posEqual(next, state.target)) {
      moves.push(next);
    }
  }
  return moves;
}

/**
 * Prunes branches that can no longer lead to a full-grid solution: each unresolved
 * head must still be able to reach its target through empty cells, and every empty
 * cell must be reachable by at least one unresolved head (otherwise it can never be
 * filled). This flood-fill check is what makes the search tractable on larger grids.
 */
function isDeadState(heads: HeadState[], grid: CellState[][], size: number): boolean {
  const reachable = new Set<string>();

  for (const h of heads) {
    if (h.resolved) continue;

    const seen = new Set<string>([keyOf(h.head)]);
    const queue: Pos[] = [h.head];
    let targetReached = false;
    let cursor = 0;

    while (cursor < queue.length) {
      const cur = queue[cursor++]!;
      for (const [dx, dy] of DIRECTIONS) {
        const next: Pos = [cur[0] + dx, cur[1] + dy];
        if (!isInBounds(next, size)) continue;
        const k = keyOf(next);
        if (seen.has(k)) continue;

        const cell = grid[next[1]][next[0]];
        const passable = cell === null || (cell === h.color && posEqual(next, h.target));
        if (!passable) continue;

        seen.add(k);
        queue.push(next);
        if (posEqual(next, h.target)) targetReached = true;
      }
    }

    if (!targetReached) return true;
    for (const k of seen) reachable.add(k);
  }

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (grid[y][x] === null && !reachable.has(keyOf([x, y]))) return true;
    }
  }

  return false;
}

/** Most-constrained-first: extend the unresolved head with the fewest legal moves. */
function pickHead(
  heads: HeadState[],
  grid: CellState[][],
  size: number,
): { head: HeadState; moves: Pos[] } | null {
  let best: HeadState | null = null;
  let bestMoves: Pos[] = [];

  for (const h of heads) {
    if (h.resolved) continue;
    const moves = legalMoves(h, grid, size);
    if (moves.length === 0) return null;
    if (best === null || moves.length < bestMoves.length) {
      best = h;
      bestMoves = moves;
      if (moves.length === 1) break;
    }
  }

  return best === null ? null : { head: best, moves: bestMoves };
}

function solveRecursive(
  grid: CellState[][],
  size: number,
  heads: HeadState[],
  filled: number,
  total: number,
  solutions: Map<ColorId, Pos[]>[],
  maxSolutions: number,
): void {
  if (solutions.length >= maxSolutions) return;

  // A head's own target cell is pre-marked in the grid from the start (see solveLevel),
  // so `filled` can reach `total` before every head has actually stepped onto its target —
  // that pre-marked cell just sits there, adjacent but unconnected. Require every head to
  // be resolved too, so a "full board" only counts once each color's path truly reaches
  // its declared endpoint rather than merely neighboring it.
  if (filled === total && heads.every((h) => h.resolved)) {
    const snapshot = new Map<ColorId, Pos[]>();
    for (const h of heads) snapshot.set(h.color, [...h.path]);
    solutions.push(snapshot);
    return;
  }

  const picked = pickHead(heads, grid, size);
  if (!picked) return;

  const { head, moves } = picked;

  for (const move of moves) {
    const [x, y] = move;
    const prevCell = grid[y][x];
    const prevHead = head.head;
    const reachedTarget = posEqual(move, head.target);
    const isNewCell = prevCell === null;

    grid[y][x] = head.color;
    head.path.push(move);
    head.head = move;
    if (reachedTarget) head.resolved = true;

    if (!isDeadState(heads, grid, size)) {
      solveRecursive(grid, size, heads, filled + (isNewCell ? 1 : 0), total, solutions, maxSolutions);
    }

    if (reachedTarget) head.resolved = false;
    head.head = prevHead;
    head.path.pop();
    grid[y][x] = prevCell;

    if (solutions.length >= maxSolutions) return;
  }
}

export function solveLevel(
  size: number,
  pairs: SolverPair[],
  maxSolutions = 2,
  walls: Pos[] = [],
): SolverResult {
  const grid = createEmptyGrid(size);
  for (const [x, y] of walls) {
    grid[y][x] = WALL;
  }
  for (const pair of pairs) {
    grid[pair.a[1]][pair.a[0]] = pair.color;
    grid[pair.b[1]][pair.b[0]] = pair.color;
  }

  const heads: HeadState[] = pairs.map((p) => ({
    color: p.color,
    head: p.a,
    target: p.b,
    path: [p.a],
    resolved: posEqual(p.a, p.b),
  }));

  const total = size * size - walls.length;
  const filled = pairs.reduce((sum, p) => sum + (posEqual(p.a, p.b) ? 1 : 2), 0);
  const solutions: Map<ColorId, Pos[]>[] = [];

  if (!isDeadState(heads, grid, size)) {
    solveRecursive(grid, size, heads, filled, total, solutions, maxSolutions);
  }

  if (solutions.length === 0) {
    return { solvable: false, unique: false, solutionCount: 0 };
  }

  return {
    solvable: true,
    unique: solutions.length === 1,
    solution: solutions[0],
    solutionCount: solutions.length,
  };
}

export function validateLevel(size: number, pairs: SolverPair[], walls: Pos[] = []): SolverResult {
  return solveLevel(size, pairs, 2, walls);
}

export function generateLevelPairs(
  size: number,
  pairCount: number,
  wallCount = 0,
  rng: RandomSource = Math.random,
): { pairs: SolverPair[]; solution: Map<ColorId, Pos[]>; walls: Pos[] } | null {
  for (let attempt = 0; attempt < 300; attempt++) {
    const generated = generateSolution(size, pairCount, wallCount, rng);
    if (!generated) continue;

    const pairs = solutionToPairs(generated.paths);
    if (pairs.length !== pairCount) continue;

    const result = solveLevel(size, pairs, 2, generated.walls);
    if (result.solvable && result.unique) {
      return { pairs, solution: generated.paths, walls: generated.walls };
    }
  }
  return null;
}
